<template>
    <div class="qi-line-items">
        <!-- Header (desktop only) -->
        <div class="d-none d-md-block">
            <div class="qi-line-header">
                <span class="qi-col-drag"></span>
                <span class="qi-col-item">Items</span>
                <span class="qi-col-qty text-right">Quantity</span>
                <span class="qi-col-price text-right">Price</span>
                <span class="qi-col-amount text-right">Amount</span>
                <span class="qi-col-remove"></span>
            </div>
        </div>

        <!-- Desktop rows -->
        <div class="d-none d-md-block qi-rows-box" v-if="items.length">
            <div
                class="qi-line-row"
                v-for="(item, idx) in items"
                :key="item._key"
                draggable="true"
                :class="{ 'qi-dragging': dragIndex === idx }"
                @dragstart="onDragStart(idx)"
                @dragover.prevent="onDragOver(idx)"
                @drop.prevent="onDrop(idx)"
                @dragend="onDragEnd"
            >
                <div class="qi-line-main">
                    <span class="qi-col-drag qi-drag-handle" title="Drag to reorder"><i class="bi bi-grip-vertical"></i></span>
                    <div class="qi-col-item">
                        <div class="font-weight-bold">{{ item.description }}</div>
                        <input type="text" class="qi-note-input" placeholder="Add a note (optional)" v-model="item.item_note" @input="emitChange">
                    </div>
                    <div class="qi-col-qty"><input type="number" min="0.01" step="0.01" class="form-control text-right" v-model.number="item.quantity" @input="emitChange"></div>
                    <div class="qi-col-price"><input type="number" min="0" step="0.01" class="form-control text-right" v-model.number="item.unit_price" @input="emitChange"></div>
                    <div class="qi-col-amount text-right font-weight-bold">{{ formatMoney(rowTotal(item)) }}</div>
                    <div class="qi-col-remove">
                        <button type="button" class="btn btn-link text-danger p-0" @click="removeItem(idx)" title="Remove item">
                            <i class="bi bi-trash"></i>
                        </button>
                    </div>
                </div>
                <div class="qi-line-extra">
                    <div class="qi-extra-discount">
                        <label class="qi-mini-label mb-0 mr-2">Discount</label>
                        <input type="number" min="0" step="0.01" class="form-control" style="width:120px;" v-model.number="item.discount" @input="emitChange">
                    </div>
                    <div class="qi-extra-tax">
                        <label class="qi-mini-label mb-0 mr-2">Tax</label>
                        <select class="form-control" style="width:190px;" v-model="item.tax_id" @change="onTaxChange(item)">
                            <option :value="null">No tax</option>
                            <option v-for="t in taxes" :key="t.id" :value="t.id">{{ t.tax }} ({{ t.tax_perc }}%)</option>
                        </select>
                    </div>
                    <div class="qi-extra-move">
                        <button type="button" class="btn btn-link text-muted p-0 mr-2" :disabled="idx === 0" @click="moveItem(idx, -1)" title="Move up"><i class="bi bi-arrow-up"></i></button>
                        <button type="button" class="btn btn-link text-muted p-0" :disabled="idx === items.length - 1" @click="moveItem(idx, 1)" title="Move down"><i class="bi bi-arrow-down"></i></button>
                    </div>
                </div>
            </div>
        </div>

        <!-- Mobile cards -->
        <div class="d-md-none">
            <div class="qi-item-card" v-for="(item, idx) in items" :key="item._key">
                <div class="d-flex justify-content-between align-items-start">
                    <div class="font-weight-bold">{{ item.description }}</div>
                    <div>
                        <button type="button" class="btn btn-link text-muted p-0 mr-2" :disabled="idx === 0" @click="moveItem(idx, -1)"><i class="bi bi-arrow-up"></i></button>
                        <button type="button" class="btn btn-link text-muted p-0 mr-2" :disabled="idx === items.length - 1" @click="moveItem(idx, 1)"><i class="bi bi-arrow-down"></i></button>
                        <button type="button" class="btn btn-link text-danger p-0" @click="removeItem(idx)">
                            <i class="bi bi-trash"></i>
                        </button>
                    </div>
                </div>
                <input type="text" class="qi-note-input mt-1" placeholder="Add a note (optional)" v-model="item.item_note" @input="emitChange">
                <div class="form-row mt-2">
                    <div class="col-4">
                        <label class="qi-mini-label">Qty</label>
                        <input type="number" min="0.01" step="0.01" class="form-control" v-model.number="item.quantity" @input="emitChange">
                    </div>
                    <div class="col-4">
                        <label class="qi-mini-label">Price</label>
                        <input type="number" min="0" step="0.01" class="form-control" v-model.number="item.unit_price" @input="emitChange">
                    </div>
                    <div class="col-4">
                        <label class="qi-mini-label">Discount</label>
                        <input type="number" min="0" step="0.01" class="form-control" v-model.number="item.discount" @input="emitChange">
                    </div>
                </div>
                <div class="form-row mt-1">
                    <div class="col-8">
                        <label class="qi-mini-label">Tax</label>
                        <select class="form-control" v-model="item.tax_id" @change="onTaxChange(item)">
                            <option :value="null">No tax</option>
                            <option v-for="t in taxes" :key="t.id" :value="t.id">{{ t.tax }} ({{ t.tax_perc }}%)</option>
                        </select>
                    </div>
                    <div class="col-4 text-right">
                        <label class="qi-mini-label d-block">Total</label>
                        <span class="font-weight-bold">{{ formatMoney(rowTotal(item)) }}</span>
                    </div>
                </div>
            </div>
        </div>

        <!--
            Add item - on its own line below the already-added items, not
            above the header - uses bootstrap-vue's <b-dropdown> (same
            primitive as BomList.vue's row-action menu, and
            CustomerPicker.vue's Add Customer control above) instead of a
            hand-rolled show/hide + document click-outside listener.
            <b-dropdown-form> wraps the search input so clicking/typing in
            it never closes the menu.
        -->
        <b-dropdown
            ref="dropdown"
            variant="link"
            no-caret
            toggle-class="qi-add-trigger"
            menu-class="qi-search-menu p-0 shadow-sm"
            class="d-block w-100 mt-2"
            @shown="onShown"
        >
            <template #button-content>
                <i class="bi bi-plus-circle-fill"></i> Add an item
            </template>

            <b-dropdown-form class="p-2" @submit.prevent>
                <div class="input-group">
                    <div class="input-group-prepend">
                        <span class="input-group-text bg-white"><i class="bi bi-search text-muted"></i></span>
                    </div>
                    <input
                        ref="searchInput"
                        type="text"
                        class="form-control"
                        placeholder="Type an item name"
                        v-model="search"
                        @input="onSearch"
                        autocomplete="off"
                    >
                </div>
            </b-dropdown-form>

            <b-dropdown-text v-if="searching" class="text-center py-3">
                <span class="spinner-border spinner-border-sm"></span>
            </b-dropdown-text>
            <template v-else>
                <b-dropdown-item-button
                    v-for="p in searchResults"
                    :key="p.id"
                    class="qi-search-result"
                    @click="addProduct(p)"
                >
                    <span class="qi-search-result-name">
                        <span class="qi-search-result-name-text">{{ p.product }}</span>
                        <small class="text-muted d-block" v-if="p.sku">{{ p.sku }}</small>
                    </span>
                    <span class="qi-search-result-price">{{ formatMoney(p.ppu) }}</span>
                </b-dropdown-item-button>
                <b-dropdown-text v-if="!searchResults.length" class="text-muted small text-center py-2">
                    No item found{{ search ? ` for "${search}"` : '' }}
                </b-dropdown-text>
            </template>

            <b-dropdown-divider></b-dropdown-divider>
            <b-dropdown-item-button class="qi-search-create" @click="openQuickAdd">
                <i class="bi bi-plus-circle mr-1"></i> Create a new item
            </b-dropdown-item-button>
        </b-dropdown>

        <product-quick-add-modal
            v-if="showQuickAdd"
            :initial-name="search"
            @close="showQuickAdd = false"
            @created="onProductCreated"
        ></product-quick-add-modal>
    </div>
</template>

<script>
import { formatMoney } from './docHelpers';
import ProductQuickAddModal from './ProductQuickAddModal.vue';

export default {
    name: 'LineItemsEditor',
    components: { ProductQuickAddModal },
    props: {
        value: { type: Array, default: () => [] },
        currency: { type: String, default: '' },
    },
    data() {
        return {
            items: (this.value || []).map(i => ({ ...i, _key: i._key || this.uid() })),
            taxes: [],
            search: '',
            searchResults: [],
            searching: false,
            showQuickAdd: false,
            debounceTimer: null,
            dragIndex: null,
        };
    },
    created() {
        axios.get('/api/taxes').then(({ data }) => {
            this.taxes = Array.isArray(data) ? data : (data.data || []);
        }).catch(() => {});
    },
    methods: {
        formatMoney(v) { return formatMoney(v, this.currency); },
        uid() {
            return 'li_' + Math.random().toString(36).slice(2, 10);
        },
        // Opening the dropdown shows a default item list immediately (not
        // just once the user types), matching the reference
        // Dukakit-Enterprise-front implementation's behavior.
        onShown() {
            this.search = '';
            this.fetchDefault();
            this.$nextTick(() => this.$refs.searchInput && this.$refs.searchInput.focus());
        },
        async fetchDefault() {
            this.searching = true;
            try {
                const { data } = await axios.get('/api/pos/search', { params: { q: '', sellable_only: 0, browse: 1 } });
                this.searchResults = data.products || [];
            } catch (e) {
                this.searchResults = [];
            } finally {
                this.searching = false;
            }
        },
        rowTotal(item) {
            const base = (Number(item.quantity) || 0) * (Number(item.unit_price) || 0) - (Number(item.discount) || 0);
            const taxPerc = item.tax ? Number(item.tax.tax_perc) : 0;
            const taxAmt = taxPerc ? base * (taxPerc / 100) : 0;
            return Math.max(0, base) + taxAmt;
        },
        resolveProductTax(p) {
            // Products carry applied tax ids in more_details.taxes (see
            // Product::getAppliedTaxesAttribute); a quote/invoice line only
            // supports one tax, so use the first configured one, if any.
            const taxIds = (p.more_details && Array.isArray(p.more_details.taxes)) ? p.more_details.taxes : [];
            const firstId = taxIds.length ? taxIds[0] : null;
            const tax = firstId ? this.taxes.find(t => t.id === firstId) || null : null;
            return { tax_id: tax ? tax.id : null, tax };
        },
        onTaxChange(item) {
            item.tax = this.taxes.find(t => t.id === item.tax_id) || null;
            this.emitChange();
        },
        emitChange() {
            this.$emit('input', this.items);
        },
        onSearch() {
            clearTimeout(this.debounceTimer);
            if (!this.search) { this.fetchDefault(); return; }
            this.searching = true;
            this.debounceTimer = setTimeout(async () => {
                try {
                    // sellable_only=0: invoice/quote line items include
                    // document-only items (can_be_sold=false — see
                    // ProductQuickAddModal's "Add to POS" toggle), not just
                    // what the actual Sales page can sell.
                    const { data } = await axios.get('/api/pos/search', { params: { q: this.search, sellable_only: 0 } });
                    this.searchResults = data.products || [];
                } catch (e) {
                    this.searchResults = [];
                } finally {
                    this.searching = false;
                }
            }, 250);
        },
        addProduct(p) {
            this.items.push({
                _key: this.uid(),
                product_id: p.id,
                description: p.product,
                item_note: '',
                quantity: 1,
                unit_price: Number(p.ppu) || 0,
                discount: 0,
                ...this.resolveProductTax(p),
            });
            this.search = '';
            this.searchResults = [];
            this.$refs.dropdown && this.$refs.dropdown.hide();
            this.emitChange();
        },
        openQuickAdd() {
            this.showQuickAdd = true;
            this.$refs.dropdown && this.$refs.dropdown.hide();
        },
        onProductCreated(product) {
            this.items.push({
                _key: this.uid(),
                product_id: product.id,
                description: product.product,
                item_note: '',
                quantity: 1,
                unit_price: product.ppu,
                discount: 0,
                tax_id: product.tax_id,
                tax: product.tax,
            });
            this.showQuickAdd = false;
            this.search = '';
            this.$toasts.push({ type: 'success', message: `"${product.product}" added to this document.` });
            this.emitChange();
        },
        removeItem(idx) {
            this.items.splice(idx, 1);
            this.emitChange();
        },
        moveItem(idx, delta) {
            const target = idx + delta;
            if (target < 0 || target >= this.items.length) return;
            const [moved] = this.items.splice(idx, 1);
            this.items.splice(target, 0, moved);
            this.emitChange();
        },
        onDragStart(idx) {
            this.dragIndex = idx;
        },
        onDragOver(idx) {
            if (this.dragIndex === null || this.dragIndex === idx) return;
            const [moved] = this.items.splice(this.dragIndex, 1);
            this.items.splice(idx, 0, moved);
            this.dragIndex = idx;
        },
        onDrop() {
            this.emitChange();
        },
        onDragEnd() {
            this.dragIndex = null;
        },
    },
};
</script>

<style scoped>
/* A global unscoped rule elsewhere in the app (employee_earning.vue) sets
   `.small { margin: 150px auto }`, which leaks onto every `.small` element
   site-wide. Neutralize it here (higher specificity than the lone class). */
.small { margin: 0; }
/* Another global unscoped rule (FullCalendar.vue) sets `li { margin: 0.5em 0 }`,
   which leaks onto every <li> site-wide - including bootstrap-vue's dropdown
   item wrappers here. Neutralize it so row spacing is controlled purely by
   our own .dropdown-item padding above. */
.qi-line-items ::v-deep li { margin: 0; }

.qi-line-items .form-control {
    border-radius: 8px;
    border-color: #d7dde3;
}
.qi-line-items .form-control:focus {
    border-color: #b8c0c8;
    box-shadow: 0 0 0 .15rem rgba(33,37,41,.08);
}
.qi-line-items .input-group-text {
    border-radius: 8px 0 0 8px;
    border-color: #d7dde3;
}
.qi-line-items .input-group .form-control {
    border-radius: 0 8px 8px 0;
}

.qi-line-header {
    display: grid;
    grid-template-columns: 28px minmax(220px,1fr) 110px 130px 130px 32px;
    gap: 14px;
    align-items: center;
    padding: 10px 12px;
    background: #f5f6f8;
    border-radius: 8px 8px 0 0;
    font-size: .74rem;
    text-transform: uppercase;
    letter-spacing: .03em;
    color: #6c757d;
    font-weight: 600;
}
.qi-rows-box {
    border: 1px solid #eee;
    border-radius: 8px;
    overflow: hidden;
}
.qi-line-row {
    border-bottom: 1px solid #eee;
    padding: 14px 12px 16px;
    background: #fff;
}
.qi-line-row:last-of-type { border-bottom: none; }
.qi-line-row.qi-dragging { opacity: .4; }
.qi-line-main {
    display: grid;
    grid-template-columns: 28px minmax(220px,1fr) 110px 130px 130px 32px;
    gap: 14px;
    align-items: start;
}
.qi-drag-handle { cursor: grab; color: #adb5bd; text-align: center; padding-top: 6px; }
.qi-drag-handle:active { cursor: grabbing; }
.qi-col-amount { padding-top: 6px; }
.qi-col-remove { text-align: center; }
.qi-note-input {
    border: none;
    border-bottom: 1px dashed transparent;
    width: 100%;
    font-size: .82rem;
    color: #6c757d;
    padding: 4px 0;
    background: transparent;
}
.qi-note-input:hover, .qi-note-input:focus { border-bottom-color: #ced4da; outline: none; }
.qi-line-extra {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 24px;
    margin-top: 12px;
    padding-left: 42px;
}
.qi-extra-discount, .qi-extra-tax { display: flex; align-items: center; }
.qi-item-card {
    border: 1px solid #eee;
    border-radius: 8px;
    padding: 12px 14px;
    margin-bottom: 10px;
}
.qi-mini-label { font-size: .68rem; color: #6c757d; margin-bottom: 2px; display: block; }

/* "Add an item" dropdown toggle, styled as a plain accent-colored link
   (matches the reference Dukakit-Enterprise-front InvoiceForm.vue's
   "+ Add Product or Service" trigger). */
.qi-line-items ::v-deep .qi-add-trigger {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    color: #fd4719 !important;
    font-size: .88rem;
    text-decoration: none !important;
    box-shadow: none !important;
    padding: 8px 2px;
}
.qi-line-items ::v-deep .qi-add-trigger:hover { text-decoration: underline !important; }

.qi-line-items ::v-deep .qi-search-menu {
    width: 100%;
    max-width: 100%;
    border-radius: 10px;
    border-color: #d7dde3;
    max-height: 360px;
    overflow-y: auto;
}
.qi-line-items ::v-deep .qi-search-menu .dropdown-item {
    padding: .5rem .9rem;
    font-size: .86rem;
    line-height: 1.3;
}
/* Name + price on one line, name truncates instead of colliding with the
   price - min-width:0 alone isn't enough on a flex child whose own
   children (the text spans) have no bound, so the name itself needs the
   overflow/ellipsis treatment, not just its wrapper. */
.qi-line-items ::v-deep .qi-search-result .dropdown-item {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 10px;
    border-bottom: 1px solid #f2f3f5;
}
.qi-line-items ::v-deep .qi-search-result-name {
    min-width: 0;
    flex: 1 1 auto;
    overflow: hidden;
}
.qi-line-items ::v-deep .qi-search-result-name-text {
    display: block;
    font-weight: 700;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.qi-line-items ::v-deep .qi-search-result-price {
    flex-shrink: 0;
    font-weight: 700;
    white-space: nowrap;
}
.qi-line-items ::v-deep .qi-search-create {
    color: #fd4719;
    font-weight: 600;
    text-align: center;
}

/* The menu already matches the trigger's width (itself full-width via the
   w-100 class above) - on a narrow screen just give it more vertical room
   and drop the corner rounding so it reads as a proper mobile panel
   rather than a cramped desktop dropdown. Not forcing position:fixed here
   - that fights Popper's own inline positioning (reapplied on scroll/
   resize) rather than cooperating with it. */
@media (max-width: 575.98px) {
    .qi-line-items ::v-deep .qi-search-menu {
        max-height: 60vh;
        border-radius: 8px;
    }
}
</style>
